import React, { useState } from 'react';
import PageWrapper from '@/components/ui/PageWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const faqs = [
  {
    q: "How long does a premium detail take?",
    a: "A standard Premium Detail usually takes between 4 to 6 hours depending on the size and condition of the vehicle. Paint correction and ceramic coating packages can take anywhere from 1 to 3 days as they require multiple stages of prep, polishing, and curing time."
  },
  {
    q: "What is a Ceramic Coating and is it worth it?",
    a: "Ceramic coating is a liquid polymer applied to the exterior of a vehicle. It chemically bonds with the factory paint, creating a layer of protection. It is absolutely worth it if you want long-lasting protection against UV rays, chemical stains, and to make washing your car significantly easier due to its hydrophobic properties."
  },
  {
    q: "Do you offer mobile detailing services?",
    a: "To maintain our extremely high standards of quality and ensure a perfectly controlled environment (especially for paint correction and coatings), all major services must be performed at our Montreal studio. We do, however, offer a valet pick-up and drop-off service for select packages."
  },
  {
    q: "Will polishing remove deep scratches?",
    a: "Paint correction (polishing/compounding) can remove light to medium scratches, swirl marks, and oxidation that are within the clear coat. If a scratch has gone through the clear coat and into the base color or primer (if your fingernail catches in it), it cannot safely be polished out and would require touch-up paint or respraying."
  },
  {
    q: "How often should I have my car detailed?",
    a: "We recommend a full deep detail (like our Premium package) at least twice a year—ideally before winter to protect against salt, and after winter to remove build-up. Regular maintenance washes should be done every 2-4 weeks."
  },
  {
    q: "Do you work on luxury and exotic vehicles?",
    a: "Yes, absolutely. Obsidian Auto Spa specializes in high-end, luxury, and exotic vehicles. Our team is trained in handling delicate materials like Alcantara, carbon fiber, and sensitive paint systems with the utmost care."
  }
];

const FAQItem = ({ faq, isOpen, onClick }) => {
  return (
    <div className="border border-white/10 rounded-xl bg-primary overflow-hidden mb-4 transition-colors hover:border-white/20">
      <button 
        className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none"
        onClick={onClick}
      >
        <span className="font-heading font-semibold text-lg pr-8">{faq.q}</span>
        <SafeIcon 
          icon={isOpen ? FiIcons.FiMinus : FiIcons.FiPlus} 
          className={`text-accent text-xl flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pb-6 text-textMuted leading-relaxed border-t border-white/5 pt-4">
              {faq.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <PageWrapper>
      <div className="py-24 bg-bgDark min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-textMuted">Everything you need to know about our detailing processes and services.</p>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <FAQItem 
                key={index} 
                faq={faq} 
                isOpen={openIndex === index} 
                onClick={() => setOpenIndex(index === openIndex ? -1 : index)} 
              />
            ))}
          </div>
          
          <div className="mt-16 text-center bg-primary p-8 rounded-2xl border border-white/5">
            <h3 className="font-heading text-2xl font-bold mb-2">Still have questions?</h3>
            <p className="text-textMuted mb-6">We're here to help. Reach out to our team directly.</p>
            <a href="mailto:hello@obsidianautospa.ca" className="btn-outline">Contact Support</a>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default FAQ;